/**
 * The staffing panel on a contract (wave b12 frontend item 9; wave CRED ruling 20): the
 * contract's staffing rules (how many qualified people a role needs, per discipline), how
 * many it has now and the shortfall, and the act that adds a rule.
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * The counts are the server's (`qualified_count`: the people in the role whose required
 * qualifications are VALID today). An ended contract takes no new rule (409
 * staffing.contract_state, after the permission), the same rule again is 409
 * staffing.rule_exists; both are said inside the dialog in the server's words. The
 * credential requirement's `enforced` flag has no route yet: when a read carries it as
 * false, the line says the requirement is switched off; until then it says nothing.
 */

import { useState } from "react";

import { ApiError } from "../api/client";
import { addStaffingRule, readStaffing, type StaffingLine } from "../api/credentials";
import type { TaxonomyToken } from "../api/portfolio";
import { strings } from "../brand/strings";
import { ServerField } from "../lib/serverErrors";
import { EmptyState, Section, SkeletonRows } from "../screens/parts";
import { useQuery } from "../screens/useQuery";
import { DataTable, type RegisterColumn } from "@/components/ui/data-table";
import { ActionDialog } from "./ActionDialog";
import { readColleagues } from "./FrontDoorDialogs";

const copy = strings.credentials;

const COLUMNS: readonly RegisterColumn<StaffingLine>[] = [
  { id: "role", label: copy.staffingColumnRole, text: (row) => row.roleLabel, filter: true },
  {
    id: "discipline",
    label: copy.staffingColumnDiscipline,
    text: (row) => row.disciplineLabel ?? copy.staffingEveryDiscipline,
    filter: true,
  },
  {
    id: "needed",
    label: copy.staffingColumnNeeded,
    text: (row) => String(row.minCount),
    sortValue: (row) => row.minCount,
  },
  {
    id: "qualified",
    label: copy.staffingColumnQualified,
    text: (row) => String(row.qualifiedCount),
    sortValue: (row) => row.qualifiedCount,
  },
  {
    id: "shortfall",
    label: copy.staffingColumnShortfall,
    text: (row) => (row.shortfall > 0 ? copy.staffingShort(row.shortfall) : copy.staffingMet),
    sortValue: (row) => row.shortfall,
    render: (row) => (
      <span className="flex flex-col gap-1">
        <span
          className={`q-chip${row.shortfall > 0 ? " q-chip--danger" : " q-chip--success"}`}
          data-testid="staffing-shortfall"
        >
          {row.shortfall > 0 ? copy.staffingShort(row.shortfall) : copy.staffingMet}
        </span>
        {row.enforced === false && (
          <span className="text-xs text-muted-foreground" data-testid="staffing-enforced-off">
            {copy.enforcedOff}
          </span>
        )}
      </span>
    ),
  },
];

export function StaffingPanel({
  token,
  contractId,
  disciplines,
  onAdded,
}: {
  readonly token: string;
  readonly contractId: string;
  /** The contract's disciplines, offered for a rule (empty: every discipline only). */
  readonly disciplines: readonly TaxonomyToken[];
  readonly onAdded: (message: string) => void;
}): React.JSX.Element {
  const [stamp, setStamp] = useState(0);
  const [adding, setAdding] = useState(false);
  const staffing = useQuery(
    (signal) => readStaffing(token, contractId, signal),
    [token, contractId, stamp],
  );
  return (
    <Section heading={copy.staffingHeading} lede={copy.staffingLede}>
      <div className="q-ui flex min-w-0 flex-col gap-3" data-testid="staffing-panel">
        {staffing.kind === "loading" && <SkeletonRows />}
        {(staffing.kind === "failed" || staffing.kind === "absence") && (
          <div className="q-refusal" role="alert">
            <p className="q-refusal__heading">{strings.common.failed}</p>
          </div>
        )}
        {staffing.kind === "ready" && staffing.data.summary !== null && (
          <p className="q-note" data-testid="staffing-summary">
            {staffing.data.summary}
          </p>
        )}
        {staffing.kind === "ready" &&
          (staffing.data.lines.length === 0 ? (
            <EmptyState title={copy.staffingHeading} body={copy.staffingEmpty} />
          ) : (
            <DataTable
              data={staffing.data.lines}
              columns={COLUMNS}
              caption={copy.staffingCaption}
              testId="staffing"
              getRowId={(row) => row.ruleId}
              getRowTestId={() => "staffing-row"}
            />
          ))}
        <div className="q-actions-row">
          <button
            type="button"
            className="q-button q-button--quiet"
            data-testid="staffing-add"
            onClick={() => {
              setAdding(true);
            }}
          >
            {copy.staffingAdd}
          </button>
        </div>
      </div>
      {adding && (
        <StaffingRuleDialog
          token={token}
          contractId={contractId}
          disciplines={disciplines}
          onClose={() => {
            setAdding(false);
          }}
          onDone={() => {
            setAdding(false);
            setStamp((one) => one + 1);
            onAdded(copy.staffingAdded);
          }}
        />
      )}
    </Section>
  );
}

function StaffingRuleDialog({
  token,
  contractId,
  disciplines,
  onClose,
  onDone,
}: {
  readonly token: string;
  readonly contractId: string;
  readonly disciplines: readonly TaxonomyToken[];
  readonly onClose: () => void;
  readonly onDone: () => void;
}): React.JSX.Element {
  const colleagues = useQuery((signal) => readColleagues(token, signal), [token]);
  const [roleId, setRoleId] = useState("");
  const [disciplineId, setDisciplineId] = useState("");
  const [count, setCount] = useState("1");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const whole = /^\d+$/u.test(count.trim()) && Number(count) >= 1 && Number(count) <= 500;
  const ready = roleId !== "" && whole;

  async function send(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await addStaffingRule(token, contractId, {
        role_id: roleId,
        discipline_id: disciplineId === "" ? null : disciplineId,
        min_count: Number(count),
      });
      onDone();
    } catch (caught) {
      if (caught instanceof ApiError) {
        setError(caught);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <ActionDialog open title={copy.staffingAddHeading} onClose={onClose} error={error}>
      <div className="q-field">
        <label className="q-field__label" htmlFor="staffing-role">
          {copy.staffingRoleLabel}
        </label>
        <ServerField field="role_id">
          <select
            id="staffing-role"
            className="q-input"
            data-testid="staffing-role"
            value={roleId}
            onChange={(event) => {
              setRoleId(event.target.value);
            }}
          >
            <option value="">{copy.staffingRoleChoose}</option>
            {(colleagues.kind === "ready" ? colleagues.data.roles : []).map((one) => (
              <option key={one.id} value={one.id}>
                {one.label}
              </option>
            ))}
          </select>
        </ServerField>
        <p className="q-note">{copy.staffingRoleHint}</p>
      </div>
      <div className="q-field">
        <label className="q-field__label" htmlFor="staffing-discipline">
          {copy.staffingDisciplineLabel}
        </label>
        <ServerField field="discipline_id">
          <select
            id="staffing-discipline"
            className="q-input"
            data-testid="staffing-discipline"
            value={disciplineId}
            onChange={(event) => {
              setDisciplineId(event.target.value);
            }}
          >
            <option value="">{copy.staffingEveryDiscipline}</option>
            {disciplines
              .filter((one) => one.id !== undefined)
              .map((one) => (
                <option key={one.id} value={one.id}>
                  {one.label}
                </option>
              ))}
          </select>
        </ServerField>
      </div>
      <div className="q-field">
        <label className="q-field__label" htmlFor="staffing-count">
          {copy.staffingCountLabel}
        </label>
        <ServerField field="min_count">
          <input
            id="staffing-count"
            className="q-input"
            type="number"
            inputMode="numeric"
            min="1"
            max="500"
            step="1"
            data-testid="staffing-count"
            aria-describedby="staffing-count-hint"
            value={count}
            onChange={(event) => {
              setCount(event.target.value);
            }}
          />
        </ServerField>
        <p className="q-note" id="staffing-count-hint">
          {copy.staffingCountHint}
        </p>
      </div>
      <div className="q-actions-row">
        <button
          type="button"
          className="q-button q-mini--primary"
          data-testid="staffing-send"
          disabled={busy || !ready}
          onClick={() => {
            void send();
          }}
        >
          {copy.staffingSend}
        </button>
        <button type="button" className="q-button q-button--quiet" onClick={onClose}>
          {strings.common.cancel}
        </button>
      </div>
    </ActionDialog>
  );
}
